/**
 * Invite User Dialog
 *
 * Reason: Allows admins to invite new users to their organization
 * with specified role (admin, user, viewer)
 */

import { FC, useState, useContext } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  MenuItem,
  Alert,
  CircularProgress,
  Box,
} from '@mui/material';
import { useQueryClient } from 'react-query';
import { useInviteOrgUser, LIST_ORG_USERS_CACHE_KEY } from '@/adapters/users-v2';
import { UserContext } from '@/contexts/UserContext';

interface InviteUserDialogProps {
  open: boolean;
  onClose(): void;
}

/**
 * Dialog for inviting new users to organization
 *
 * @reason Provides form UI for admins to invite users with role selection
 */
const InviteUserDialog: FC<InviteUserDialogProps> = ({ open, onClose }) => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<'admin' | 'manager' | 'viewer'>('manager');
  const [error, setError] = useState<string | null>(null);

  const { userState } = useContext(UserContext);
  const queryClient = useQueryClient();
  const inviteUserMutation = useInviteOrgUser(userState?.orgId);

  /**
   * Handles form submission to invite new user
   *
   * @reason Invites user and refreshes the user list
   */
  const handleInvite = async (): Promise<void> => {
    setError(null);

    // Basic validation
    if (!email.trim()) {
      setError('Email is required');
      return;
    }

    if (!email.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }

    try {
      await inviteUserMutation.mutateAsync({
        email: email.trim(),
        name: name.trim() || undefined,
        role,
      });

      // Invalidate user list to refresh
      await queryClient.invalidateQueries(LIST_ORG_USERS_CACHE_KEY);

      // Close dialog and reset form
      handleClose();
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to invite user';
      setError(errorMessage);
    }
  };

  /**
   * Handles dialog close and resets form
   *
   * @reason Cleans up form state when dialog is closed
   */
  const handleClose = (): void => {
    setEmail('');
    setName('');
    setRole('manager');
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Invite User</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TextField
          autoFocus
          margin="dense"
          label="Email"
          type="email"
          fullWidth
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={inviteUserMutation.isLoading}
          sx={{ mb: 2 }}
        />

        <TextField
          margin="dense"
          label="Name"
          type="text"
          fullWidth
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={inviteUserMutation.isLoading}
          sx={{ mb: 2 }}
        />

        <TextField
          margin="dense"
          label="Role"
          select
          fullWidth
          required
          value={role}
          onChange={(e) => setRole(e.target.value as 'admin' | 'manager' | 'viewer')}
          disabled={inviteUserMutation.isLoading}
        >
          <MenuItem value="admin">Admin - Full access to organization</MenuItem>
          <MenuItem value="manager">Manager - Can edit events and projects</MenuItem>
          <MenuItem value="viewer">Viewer - Read-only access</MenuItem>
        </TextField>

        {inviteUserMutation.isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <CircularProgress />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={inviteUserMutation.isLoading}>
          Cancel
        </Button>
        <Button onClick={handleInvite} variant="contained" disabled={inviteUserMutation.isLoading}>
          Invite
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InviteUserDialog;
